import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronUp, ChevronLeft, ChevronRight, List, Loader2 } from "lucide-react";
import api, { resolveImg, API } from "../lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { SubscribeForm } from "../components/SubscribeForm";
import { useKahaniDrift, KahaniDriftButton } from "../components/KahaniDrift";
import { BINGE_SERIES } from "../lib/binge";

function LazyPanel({ panel, index }) {
  const ref = useRef(null);
  const imgRef = useRef(null);
  const [visible, setVisible] = useState(index < 3); // eager-load first few
  const [loaded, setLoaded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retryRef = useRef(0);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: "1500px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  // Catch already-cached images whose onLoad may not fire
  useEffect(() => {
    if (visible && imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [visible]);

  const handleError = () => {
    if (retryRef.current < 3) {
      retryRef.current += 1;
      setTimeout(() => setAttempt((a) => a + 1), 600 * retryRef.current);
    }
  };

  const src = resolveImg(panel.image_url) + (attempt > 0 ? `?r=${attempt}` : "");

  return (
    <div
      ref={ref}
      data-testid={`reader-panel-${index}`}
      className="relative"
      style={!loaded ? { minHeight: "78vh" } : undefined}
    >
      {visible && (
        <img
          ref={imgRef}
          src={src}
          alt={panel.alt_text || `Panel ${index + 1}`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={handleError}
          className="fade-panel"
        />
      )}
      {!loaded && <div className="panel-skeleton absolute inset-0" />}
      {panel.is_title && (
        <div className="title-overlay" data-testid="reader-title-overlay">
          <h1 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight text-white drop-shadow-lg">
            {panel.title_text}
          </h1>
          {panel.subtitle_text && (
            <p className="mt-2 font-mono-kh text-sm sm:text-base uppercase tracking-widest text-violet-400">
              {panel.subtitle_text}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function ChapterReader() {
  const { slug, num } = useParams();
  const navigate = useNavigate();
  const preview = new URLSearchParams(window.location.search).get("preview") === "1";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [allChapters, setAllChapters] = useState([]);
  const [hudVisible, setHudVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [twoUp, setTwoUp] = useState(() => localStorage.getItem("kahani_page_mode") !== "1");
  const setPageMode = (v) => {
    localStorage.setItem("kahani_page_mode", v);
    setTwoUp(v === "2");
  };
  const lastScrollY = useRef(0);
  const prefetchedRef = useRef(false);
  const laneRef = useRef(null);
  const endRef = useRef(null);
  const drift = useKahaniDrift(laneRef, endRef, `${slug}/${num}`);

  const chapterUrl = (n) => (preview ? `/admin/preview/${slug}/chapter/${n}` : `/series/${slug}/chapter/${n}`);

  const loadChapter = useCallback(() => {
    setLoading(true);
    setNotFound(false);
    prefetchedRef.current = false;
    api
      .get(chapterUrl(num))
      .then((res) => {
        setData(res.data);
        if (preview && res.data.chapters) setAllChapters(res.data.chapters);
        window.scrollTo(0, 0);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, num]);

  useEffect(() => {
    loadChapter();
  }, [loadChapter]);

  useEffect(() => {
    if (preview) return; // preview populates chapters from its own response
    api.get(`/series/${slug}`).then((res) => setAllChapters(res.data.chapters || [])).catch(() => {});
  }, [slug]);

  const goToChapter = useCallback(
    (chapterNumber) => {
      if (chapterNumber === null || chapterNumber === undefined) return;
      navigate(`/series/${slug}/chapter/${chapterNumber}${preview ? "?preview=1" : ""}`);
    },
    [navigate, slug]
  );

  // Scroll: HUD auto-hide + progress + prefetch adjacent chapter
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docH > 0 ? (y / docH) * 100 : 0;
      setProgress(pct);

      if (y < window.innerHeight * 0.1) {
        setHudVisible(true);
      } else if (y > lastScrollY.current + 8) {
        setHudVisible(false);
      } else if (y < lastScrollY.current - 8) {
        setHudVisible(true);
      }
      lastScrollY.current = y;

      // Background prefetch of next chapter panels once past 50%
      if (!prefetchedRef.current && pct > 50 && data?.next_chapter_number != null) {
        prefetchedRef.current = true;
        prefetchNext(data.next_chapter_number);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [data]);

  const prefetchNext = (nextNum) => {
    api
      .get(chapterUrl(nextNum))
      .then((res) => {
        (res.data.panels || []).forEach((p) => {
          const img = new Image();
          img.src = resolveImg(p.image_url);
        });
      })
      .catch(() => {});
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "j" || e.key === "ArrowDown") {
        window.scrollBy({ top: 300, behavior: "smooth" });
      } else if (e.key === "k" || e.key === "ArrowUp") {
        window.scrollBy({ top: -300, behavior: "smooth" });
      } else if (e.key === "Escape" && !document.querySelector('[role="menu"]')) {
        navigate(`/series/${slug}`);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, slug]);

  if (loading)
    return (
      <div className="min-h-screen bg-[#141416] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
      </div>
    );

  if (notFound || !data)
    return (
      <div className="min-h-screen bg-[#141416] flex flex-col items-center justify-center text-center">
        <p className="font-display text-2xl text-white">Chapter not found</p>
        <Link to={`/series/${slug}`} className="text-rose-400 mt-4">← Back to series</Link>
      </div>
    );

  const { series, chapter, panels, next_chapter_number, prev_chapter_number } = data;
  const chNum = formatNum(chapter.chapter_number);

  return (
    <div className="min-h-screen" style={{ background: "#141416" }}>
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-0.5 bg-transparent">
        <div className="h-full bg-rose-500 transition-[width] duration-150" style={{ width: `${progress}%` }} />
      </div>

      {/* Top HUD */}
      <header
        className={`fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-black/80 border-b border-white/10 hud-transition ${
          hudVisible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
        }`}
      >
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <button
            data-testid="reader-back-btn"
            onClick={() => navigate(preview ? "/admin" : `/series/${slug}`)}
            className="inline-flex items-center gap-2 text-sm text-gray-200 hover:text-white transition-colors shrink-0"
          >
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">{preview ? "Back to Admin" : series.title}</span>
          </button>

          <div className="text-center min-w-0">
            <p className="text-white text-sm font-semibold truncate">
              {preview && <span className="text-amber-400 font-mono-kh text-[10px] mr-1.5 px-1.5 py-0.5 rounded bg-amber-400/15 border border-amber-400/30 align-middle" data-testid="preview-badge">PREVIEW</span>}
              Ch {chNum}{chapter.title ? ` · ${chapter.title}` : ""}
            </p>
            <p className="font-mono-kh text-[10px] text-gray-400 uppercase tracking-widest">
              {Math.round(progress)}% read
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
          <div data-testid="page-mode-switch" className="hidden md:inline-flex rounded-full bg-white/5 border border-white/10 p-0.5 text-xs font-semibold">
            {[["1", "1 page"], ["2", "2 pages"]].map(([v, label]) => (
              <button
                key={v}
                data-testid={`page-mode-${v}`}
                onClick={() => setPageMode(v)}
                className={`px-3 py-1 rounded-full transition-colors ${(twoUp ? "2" : "1") === v ? "bg-[#FF7A1A] text-black" : "text-gray-300 hover:text-white"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="chapter-selector-btn"
                className="inline-flex items-center gap-1.5 text-sm text-gray-200 hover:text-white bg-white/5 border border-white/10 px-3 py-1.5 rounded-full transition-colors shrink-0"
              >
                <List className="w-4 h-4" /> <span className="hidden sm:inline">Chapters</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#12141D] border-white/10 text-gray-200 max-h-80 overflow-y-auto w-56"
            >
              {allChapters.map((c) => (
                <DropdownMenuItem
                  key={c.id}
                  data-testid={`chapter-dropdown-${formatNum(c.chapter_number)}`}
                  onClick={() => goToChapter(c.chapter_number)}
                  className={`cursor-pointer focus:bg-rose-600/20 focus:text-white ${
                    c.chapter_number === chapter.chapter_number ? "text-rose-400" : ""
                  }`}
                >
                  <span className="font-display font-bold w-10">{formatNum(c.chapter_number)}</span>
                  <span className="truncate">{c.title || `Chapter ${formatNum(c.chapter_number)}`}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Seamless vertical reading lane */}
      <main className="pt-14 pb-24">
        {panels.length === 0 ? (
          <div className="text-center py-32 text-gray-500 font-mono-kh">This chapter has no panels yet.</div>
        ) : (
          <>
          <div className="flex justify-center items-center gap-5 py-3" data-testid="kahani-drift-bar">
            <KahaniDriftButton state={drift.state} onToggle={drift.toggle} />
            {BINGE_SERIES.includes(slug) && (
              <Link to={`/series/${slug}/all`} data-testid="read-all-episodes-link" className="text-sm text-gray-300 hover:text-white underline underline-offset-4">
                Read all episodes
              </Link>
            )}
          </div>
          <div ref={laneRef} className={`reader-lane ${twoUp ? "two-up" : ""}`} data-testid="reader-lane">
            {panels.map((p, i) => (
              <LazyPanel key={p.id} panel={p} index={i} />
            ))}
          </div>
          </>
        )}

        {/* End of chapter navigation */}
        <div ref={endRef} data-testid="reader-end-card" className="max-w-3xl mx-auto px-4 mt-8 flex items-center justify-between gap-3">
          <button
            data-testid="end-prev-chapter-btn"
            disabled={prev_chapter_number == null}
            onClick={() => goToChapter(prev_chapter_number)}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl border border-white/10 bg-[#1A1D2B] text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:border-rose-500/40 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <button
            data-testid="end-next-chapter-btn"
            disabled={next_chapter_number == null}
            onClick={() => {
              if (next_chapter_number == null) {
                toast.info("You're all caught up!");
              } else {
                goToChapter(next_chapter_number);
              }
            }}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-semibold disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Next Chapter <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {!preview && (
          <div className="max-w-3xl mx-auto px-4 mt-10">
            <SubscribeForm source={`${slug} ch ${chNum}`} />
          </div>
        )}
      </main>

      {drift.state !== "off" && (
        <div className="fixed right-3 bottom-20 z-50 opacity-80 hover:opacity-100 transition-opacity" data-testid="kahani-drift-floating">
          <KahaniDriftButton state={drift.state} onToggle={drift.toggle} testId="kahani-drift-floating-btn" compact />
        </div>
      )}

      {/* Bottom sticky control bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl bg-black/80 border-t border-white/10 hud-transition ${
          hudVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
        }`}
      >
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <button
            data-testid="reader-prev-chapter-btn"
            disabled={prev_chapter_number == null}
            onClick={() => goToChapter(prev_chapter_number)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-200 disabled:opacity-30 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>

          <button
            data-testid="reader-scroll-top-btn"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-gray-200 hover:text-white hover:bg-white/10 px-5 py-2 rounded-full text-sm transition-colors"
          >
            <ChevronUp className="w-4 h-4" /> Top
          </button>

          <button
            data-testid="reader-next-chapter-btn"
            disabled={next_chapter_number == null}
            onClick={() => goToChapter(next_chapter_number)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-200 disabled:opacity-30 hover:text-white transition-colors"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function formatNum(n) {
  return String(n);
}
